/**
 * The listing form's state and its validation.
 *
 * The rules mirror the ones the API enforces — the fixed taxonomy, the field
 * limits from the shared vocabulary, and the required salary range — so a
 * company is told what is missing while it types rather than after it submits.
 * The server remains the authority; this is the courtesy layer in front of it.
 */

import {
  COMPENSATION_PERIODS,
  EMPLOYMENT_TYPES,
  JD_LIMITS,
  SENIORITY_LEVELS,
  VISA_SPONSORSHIP_OPTIONS,
  WORKPLACE_TYPES,
  isValidScope,
  type Compensation,
  type CompensationPeriod,
  type EmploymentType,
  type Job,
  type JobInput,
  type JobLocation,
  type ParsedJobFields,
  type Seniority,
  type VisaSponsorship,
  type WorkplaceType,
} from '@bidtohire/shared';

export interface LocationRow {
  readonly city: string;
  readonly region: string;
  readonly countryIso2: string;
}

export interface JobFormState {
  title: string;
  category: string;
  subcategory: string;
  applyUrl: string;
  descriptionSummary: string;
  descriptionText: string;
  employmentType: EmploymentType[];
  workplaceType: WorkplaceType;
  locations: LocationRow[];
  /**
   * Countries a remote role may be worked from, as a comma-separated list of
   * two-letter codes. Asked for only when the role is remote, and required
   * then: a remote posting that does not say where it can be worked from is
   * ineligible for Google's jobs experience outright.
   */
  remoteCountries: string;
  seniority: Seniority | '';
  experienceYearsMin: string;
  salaryMin: string;
  salaryMax: string;
  salaryCurrency: string;
  salaryPeriod: CompensationPeriod;
  salaryEstimated: boolean;
  benefits: string[];
  skills: string;
  tags: string;
  visaSponsorship: VisaSponsorship;
  openings: string;
}

export const EMPTY_LOCATION: LocationRow = { city: '', region: '', countryIso2: '' };

export function emptyJobForm(): JobFormState {
  return {
    title: '',
    category: '',
    subcategory: '',
    applyUrl: '',
    descriptionSummary: '',
    descriptionText: '',
    employmentType: ['FULL_TIME'],
    workplaceType: 'ONSITE',
    locations: [{ ...EMPTY_LOCATION }],
    remoteCountries: '',
    seniority: '',
    experienceYearsMin: '',
    salaryMin: '',
    salaryMax: '',
    salaryCurrency: 'USD',
    salaryPeriod: 'YEAR',
    salaryEstimated: false,
    benefits: [],
    skills: '',
    tags: '',
    visaSponsorship: 'UNSPECIFIED',
    openings: '1',
  };
}

/** Re-open an existing listing in the form. */
export function jobFormFrom(job: Job): JobFormState {
  const base = emptyJobForm();
  const compensation = job.compensation;
  return {
    ...base,
    title: job.title,
    category: job.category,
    subcategory: job.subcategory,
    applyUrl: job.applyUrl,
    descriptionSummary: job.descriptionSummary ?? '',
    descriptionText: htmlToText(job.descriptionHtml),
    employmentType: [...job.employmentType],
    workplaceType: job.workplaceType,
    locations:
      job.locations.length === 0
        ? [{ ...EMPTY_LOCATION }]
        : job.locations.map((location) => ({
            city: location.city ?? '',
            region: location.region ?? '',
            countryIso2: location.countryIso2,
          })),
    remoteCountries: (job.applicantLocationRequirements ?? [])
      .filter((requirement) => requirement.type === 'COUNTRY')
      .map((requirement) => requirement.code)
      .join(', '),
    seniority: job.seniority,
    experienceYearsMin: job.experienceYearsMin === undefined ? '' : String(job.experienceYearsMin),
    salaryMin: compensation?.minCents === undefined ? '' : String(compensation.minCents / 100),
    salaryMax: compensation?.maxCents === undefined ? '' : String(compensation.maxCents / 100),
    salaryCurrency: compensation?.currency ?? 'USD',
    salaryPeriod: compensation?.period ?? 'YEAR',
    salaryEstimated: compensation?.isEstimated ?? false,
    benefits: [...(job.benefits ?? [])],
    skills: (job.skills ?? []).join(', '),
    tags: (job.tags ?? []).join(', '),
    visaSponsorship: job.visaSponsorship ?? 'UNSPECIFIED',
    openings: String(job.openings),
  };
}

/**
 * Open the form on what was read off a posting's own page.
 *
 * Everything here is a suggestion: the company sees every field before it is
 * submitted, and an import that answered nothing leaves the form exactly as it
 * would have been typed into from scratch. Absent means absent — a field the
 * page did not state is left blank rather than filled with a plausible guess,
 * which is the difference between a prefilled form and a fabricated one.
 */
export function jobFormFromParsed(fields: ParsedJobFields): JobFormState {
  const base = emptyJobForm();
  const compensation = fields.compensation;
  const scope =
    fields.category !== undefined &&
    fields.subcategory !== undefined &&
    isValidScope(fields.category, fields.subcategory)
      ? { category: fields.category, subcategory: fields.subcategory }
      : undefined;

  const locations = (fields.locations ?? []).map((location) => ({
    city: location.city ?? '',
    region: location.region ?? '',
    countryIso2: location.countryIso2,
  }));

  return {
    ...base,
    title: fields.title ?? base.title,
    category: scope?.category ?? base.category,
    subcategory: scope?.subcategory ?? base.subcategory,
    applyUrl: fields.applyUrl,
    descriptionSummary: fields.descriptionSummary ?? base.descriptionSummary,
    descriptionText:
      fields.descriptionHtml === undefined
        ? base.descriptionText
        : htmlToText(fields.descriptionHtml),
    employmentType:
      fields.employmentType === undefined || fields.employmentType.length === 0
        ? base.employmentType
        : [...fields.employmentType],
    workplaceType: fields.workplaceType ?? base.workplaceType,
    locations: locations.length === 0 ? base.locations : locations,
    remoteCountries: (fields.applicantLocationRequirements ?? [])
      .filter((requirement) => requirement.type === 'COUNTRY')
      .map((requirement) => requirement.code)
      .join(', '),
    seniority: fields.seniority ?? base.seniority,
    experienceYearsMin:
      fields.experienceYearsMin === undefined ? '' : String(fields.experienceYearsMin),
    salaryMin: compensation?.minCents === undefined ? '' : String(compensation.minCents / 100),
    salaryMax: compensation?.maxCents === undefined ? '' : String(compensation.maxCents / 100),
    salaryCurrency: compensation?.currency ?? base.salaryCurrency,
    salaryPeriod: compensation?.period ?? base.salaryPeriod,
    salaryEstimated: compensation?.isEstimated ?? false,
    benefits: [...(fields.benefits ?? [])],
    skills: (fields.skills ?? []).join(', '),
    tags: (fields.tags ?? []).join(', '),
    visaSponsorship: fields.visaSponsorship ?? base.visaSponsorship,
    openings: fields.openings === undefined ? base.openings : String(fields.openings),
  };
}

/**
 * The names the import reports, in the words the form uses for them. An import
 * that says it filled in `applicantLocationRequirements` has told the company
 * nothing.
 */
const PARSED_FIELD_LABELS: Readonly<Record<string, string>> = {
  title: 'the job title',
  category: 'the category',
  subcategory: 'the specialism',
  applyUrl: 'the apply link',
  descriptionHtml: 'the description',
  descriptionSummary: 'the summary',
  employmentType: 'the employment type',
  workplaceType: 'the workplace',
  locations: 'the locations',
  applicantLocationRequirements: 'where it can be worked from',
  seniority: 'the level',
  experienceYearsMin: 'the experience required',
  compensation: 'the salary range',
  benefits: 'the benefits',
  skills: 'the skills',
  tags: 'the tags',
  visaSponsorship: 'visa sponsorship',
  openings: 'the number of openings',
};

export function parsedFieldLabel(name: string): string {
  return PARSED_FIELD_LABELS[name] ?? name;
}

/** "a, b and c" — the list read aloud rather than punctuated at. */
export function fieldSentence(names: readonly string[]): string {
  const labels = names.map(parsedFieldLabel);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0] as string;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1] as string}`;
}

const BLOCK_BREAK = /<\/(?:p|div|li|h[1-6]|blockquote)>/gi;

/** Recover editable text from stored markup, for the edit form. */
export function htmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(BLOCK_BREAK, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Turn what the company typed into the modest markup the listing renders:
 * blank-line-separated paragraphs, and lines beginning with a dash as a list.
 */
export function textToHtml(text: string): string {
  const blocks = text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block !== '');

  return blocks
    .map((block) => {
      const lines = block.split('\n').map((line) => line.trim());
      const isList = lines.every((line) => /^[-*•]\s+/.test(line));
      if (isList) {
        const items = lines
          .map((line) => `<li>${escapeHtml(line.replace(/^[-*•]\s+/, ''))}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }
      return `<p>${lines.map(escapeHtml).join('<br />')}</p>`;
    })
    .join('');
}

export type FieldErrors = Readonly<Record<string, string>>;

export type JobFormResult =
  | { readonly ok: true; readonly input: JobInput }
  | { readonly ok: false; readonly errors: FieldErrors };

export function parseWholeNumber(value: string): number | undefined {
  const trimmed = value.trim().replace(/,/g, '');
  if (trimmed === '') return undefined;
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number(trimmed);
}

function splitList(value: string, limit: number): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '')
    .slice(0, limit);
}

export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Validate the form and produce the request body, or the messages to show. */
export function toJobInput(state: JobFormState): JobFormResult {
  const errors: Record<string, string> = {};

  const title = state.title.trim();
  if (title === '') errors['title'] = 'Give the role a title.';
  else if (title.length > JD_LIMITS.titleMaxLength) {
    errors['title'] = `Titles are at most ${JD_LIMITS.titleMaxLength} characters.`;
  }

  if (state.category === '') errors['category'] = 'Choose a category.';
  if (state.subcategory === '') errors['subcategory'] = 'Choose a specialism.';
  if (
    state.category !== '' &&
    state.subcategory !== '' &&
    !isValidScope(state.category, state.subcategory)
  ) {
    errors['subcategory'] = 'Choose a specialism from the list.';
  }

  const applyUrl = state.applyUrl.trim();
  if (applyUrl === '') errors['applyUrl'] = 'Link to the posting candidates should apply on.';
  else if (!isHttpUrl(applyUrl)) errors['applyUrl'] = 'Enter a full link, beginning http:// or https://.';

  const descriptionText = state.descriptionText.trim();
  if (descriptionText.length < 40) {
    errors['descriptionText'] = 'A job description must be at least 40 characters.';
  }

  const summary = state.descriptionSummary.trim();
  if (summary.length > JD_LIMITS.summaryMaxLength) {
    errors['descriptionSummary'] = `Summaries are at most ${JD_LIMITS.summaryMaxLength} characters.`;
  }

  if (state.employmentType.length === 0) {
    errors['employmentType'] = 'Choose at least one employment type.';
  }

  if (state.seniority === '') errors['seniority'] = 'Choose a level.';

  const locations: JobLocation[] = state.locations
    .filter((location) => location.countryIso2.trim() !== '' || location.city.trim() !== '')
    .map((location) => ({
      ...(location.city.trim() === '' ? {} : { city: location.city.trim() }),
      ...(location.region.trim() === '' ? {} : { region: location.region.trim() }),
      countryIso2: location.countryIso2.trim().toUpperCase(),
    }));

  if (locations.some((location) => !/^[A-Z]{2}$/.test(location.countryIso2))) {
    errors['locations'] = 'Enter each country as a two-letter code, such as US or GB.';
  }
  if (state.workplaceType !== 'REMOTE' && locations.length === 0) {
    errors['locations'] = 'On-site and hybrid roles need at least one location.';
  }
  if (locations.length > JD_LIMITS.maxLocations) {
    errors['locations'] = `A listing may name at most ${JD_LIMITS.maxLocations} locations.`;
  }

  // "Remote" on its own is not an answer to "remote from where", and a search
  // engine treats the missing half as a disqualification rather than a gap.
  const remoteCountries = splitList(state.remoteCountries, JD_LIMITS.maxLocations).map((code) =>
    code.toUpperCase(),
  );
  if (state.workplaceType === 'REMOTE') {
    if (remoteCountries.length === 0) {
      errors['remoteCountries'] = 'Name at least one country this role can be worked from.';
    } else if (remoteCountries.some((code) => !/^[A-Z]{2}$/.test(code))) {
      errors['remoteCountries'] = 'Enter each country as a two-letter code, such as US or GB.';
    }
  }

  const minDollars = parseWholeNumber(state.salaryMin);
  const maxDollars = parseWholeNumber(state.salaryMax);
  if (minDollars === undefined || maxDollars === undefined) {
    errors['salary'] = 'A salary range is required on every listing.';
  } else if (minDollars > maxDollars) {
    errors['salary'] = 'The bottom of the range must not exceed the top.';
  }

  const openings = parseWholeNumber(state.openings) ?? 1;
  if (openings < 1) errors['openings'] = 'A listing must have at least one opening.';

  const experienceYearsMin = parseWholeNumber(state.experienceYearsMin);
  if (state.experienceYearsMin.trim() !== '' && experienceYearsMin === undefined) {
    errors['experienceYearsMin'] = 'Enter a whole number of years.';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const compensation: Compensation = {
    minCents: (minDollars as number) * 100,
    maxCents: (maxDollars as number) * 100,
    currency: state.salaryCurrency.trim().toUpperCase(),
    period: state.salaryPeriod,
    isEstimated: state.salaryEstimated,
  };

  const skills = splitList(state.skills, JD_LIMITS.maxSkills);
  const tags = splitList(state.tags, JD_LIMITS.maxTags);
  const benefits = state.benefits.slice(0, JD_LIMITS.maxBenefits);

  const input: JobInput = {
    title,
    category: state.category,
    subcategory: state.subcategory,
    applyUrl,
    descriptionHtml: textToHtml(descriptionText),
    ...(summary === '' ? {} : { descriptionSummary: summary }),
    employmentType: state.employmentType,
    workplaceType: state.workplaceType,
    locations,
    ...(state.workplaceType === 'REMOTE'
      ? {
          applicantLocationRequirements: remoteCountries.map((code) => ({
            type: 'COUNTRY' as const,
            code,
          })),
        }
      : {}),
    seniority: state.seniority as Seniority,
    ...(experienceYearsMin === undefined ? {} : { experienceYearsMin }),
    compensation,
    ...(benefits.length === 0 ? {} : { benefits }),
    ...(skills.length === 0 ? {} : { skills }),
    ...(tags.length === 0 ? {} : { tags }),
    visaSponsorship: state.visaSponsorship,
    openings,
  };

  return { ok: true, input };
}

/** Option lists for the form's selects, labelled for a customer. */
export const OPTIONS = {
  employmentTypes: EMPLOYMENT_TYPES,
  workplaceTypes: WORKPLACE_TYPES,
  seniority: SENIORITY_LEVELS,
  periods: COMPENSATION_PERIODS,
  visa: VISA_SPONSORSHIP_OPTIONS,
} as const;
